// ============================================================================
// RhfAsyncSelect.tsx
// ============================================================================

import React from "react";
import AsyncSelect from "react-select/async";
import {
  type MenuPosition,
  type MultiValue,
  type SingleValue,
} from "react-select";
import { useFormContext, useWatch } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import AxiosInstance from "@/utils/AxiosInstance";

type OptionType = {
  label: string;
  value: string;
};

type RhfAsyncSelectProps = {
  label: string;
  name: string;
  required?: boolean;
  disabled?: boolean;
  defaultvalue?: string | string[];
  placeholder?: string;
  multiple?: boolean;
  isCache?:boolean;
  portalToBody?: boolean;
  storeOptionObject?: boolean;
  asyncConfig: {
    apiUrl: string;
    searchParam?: string;
    responseMapper?: (response: any) => OptionType[];
    minSearchLength?: number;
  };
};

export const RhfAsyncSelect = ({
  label,
  name,
  required,
  disabled,
  multiple = false,
  placeholder,
  defaultvalue,
  asyncConfig,
  isCache = false,
  portalToBody = true,
  storeOptionObject = false,
}: RhfAsyncSelectProps) => {
  const { control } = useFormContext();
  const watchedValue = useWatch({ control, name });

  // Store asyncConfig in a ref to prevent infinite loops
  const asyncConfigRef = React.useRef(asyncConfig);
  
  React.useEffect(() => {
    asyncConfigRef.current = asyncConfig;
  }, [asyncConfig]);

  const resolvedPlaceholder =
    typeof placeholder === "string" && placeholder.length > 0
      ? placeholder
      : label
        ? `Select ${label.toLowerCase()} here`
        : "Select an option";

  const resolvedDefaultValue =
    defaultvalue ?? (multiple ? ([] as string[]) : "");

  const menuPortalTarget =
    portalToBody && typeof document !== "undefined" ? document.body : undefined;
  const menuPosition: MenuPosition = portalToBody ? "fixed" : "absolute";

  const loadAsyncOptions = React.useCallback(
    async (inputValue: string): Promise<OptionType[]> => {
      const config = asyncConfigRef.current;
      
      if (!config?.apiUrl) {
        return [];
      }

      const minLength = config.minSearchLength || 2;
      if (!inputValue.trim() || inputValue.trim().length < minLength) {
        return [];
      }

      try {
        const searchParam = config.searchParam || "terms";
        const url = `${config.apiUrl}?${searchParam}=${encodeURIComponent(
          inputValue,
        )}`;

        const response = await AxiosInstance().get(url);
        const data = response.data;

        // Custom mapper if provided
        if (config.responseMapper) {
          return config.responseMapper(data);
        }

        // Default ICD10 mapper: index[3] contains [[code, name], ...]
        if (Array.isArray(data) && data.length >= 4 && Array.isArray(data[3])) {
          return data[3].map((item: any[]) => ({
            value: `${item[0]} - ${item[1]}` || item[0] || "",
            label: `${item[0]} - ${item[1]}` || item[0] || "",
          }));
        }

        return [];
      } catch (error) {
        console.error("Error loading async options:", error);
        return [];
      }
    },
    [], // No dependencies - function is stable, uses ref for latest config
  );

const stylesSmall = {
  menuPortal: (base: any) => ({ ...base, zIndex: 99999 }),

  control: (base: any) => ({
    ...base,
    maxWidth: "100%",
    width: "100%",
    overflow: "hidden",
  }),

  valueContainer: (base: any) => ({
    ...base,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),

  singleValue: (base: any) => ({
    ...base,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),

  // multi select chip truncate (if used)
  multiValueLabel: (base: any) => ({
    ...base,
    maxWidth: 120,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),
};


  return (
    <FormField
      control={control}
      name={name}
      defaultValue={resolvedDefaultValue}
      render={({ field }) => {
        const selectedMultiValues = Array.isArray(field.value)
          ? field.value
          : [];

        // For async selects, create placeholder options if values exist
        const getAsyncSingleOption = (): SingleValue<OptionType> => {
          if (!multiple && storeOptionObject && field.value && typeof field.value === "object") {
            const v = field.value as OptionType;
            if (typeof v.value === "string" && typeof v.label === "string") return v;
          }
          if (!multiple && typeof field.value === "string" && field.value) {
            return { label: field.value, value: field.value };
          }
          return null;
        };

        const getAsyncMultiOptions = (): MultiValue<OptionType> => {
          if (multiple && storeOptionObject && Array.isArray(field.value)) {
            return (field.value as OptionType[]).filter(
              (v) => v && typeof v.value === "string" && typeof v.label === "string",
            );
          }
          return selectedMultiValues.map((value) => ({
            label: value,
            value,
          }));
        };

        const commonSelectProps = {
          classNamePrefix: "rhf-async-select",
          inputId: name,
          inputRef: field.ref,
          isDisabled: disabled,
          menuPortalTarget,
          menuPosition,
          menuPlacement: "auto" as const,
          menuShouldBlockScroll: false,
          closeMenuOnScroll: false,
          placeholder: resolvedPlaceholder,
          styles: stylesSmall,
          closeMenuOnSelect: !multiple,
          onBlur: field.onBlur,
          name: field.name,
          cacheOptions: isCache,
          defaultOptions: true,
          loadOptions: loadAsyncOptions,
          noOptionsMessage: ({ inputValue }: { inputValue: string }) =>
            inputValue.length < (asyncConfig.minSearchLength || 2)
              ? `Type at least ${asyncConfig.minSearchLength || 2} characters to search`
              : "No options found",
          loadingMessage: () => "Loading...",
        };

        return (
          <FormItem className="w-full">
            {label && (
              <FormLabel>
                {label}
                {required ? "*" : ""}
              </FormLabel>
            )}
            <FormControl className="w-full">
              {!multiple ? (
                <AsyncSelect<OptionType, false>
                  {...commonSelectProps}
                  value={getAsyncSingleOption()}
                  onChange={(option: SingleValue<OptionType>) => {
                    field.onChange(storeOptionObject ? (option ?? null) : (option?.value ?? ""));
                  }}
                  isClearable={true}
                />
              ) : (
                <AsyncSelect<OptionType, true>
                  {...commonSelectProps}
                  isMulti
                  value={getAsyncMultiOptions()}
                  onChange={(option: MultiValue<OptionType>) => {
                    field.onChange(
                      storeOptionObject ? option : option.map((opt) => opt.value),
                    );
                  }}
                  isClearable={true}

                />
              )}
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

export default RhfAsyncSelect;